import {
  TappSelect,
  TappSelectOption,
  TappSelectOptionValue,
  TappSelectProps,
  findOptionByValue,
} from '@/components/ui/TappSelect/Select';
import { useNavigateSearch } from 'src/components/ui/hooks/navigate-search.hook';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GroupBase, MultiValue, OnChangeValue } from 'react-select';

export type TappSelectFilterProps<
  Option extends TappSelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
> = {
  name: string;
} & TappSelectProps<Option, IsMulti, Group>;

function TappSelectFilter<
  Option extends TappSelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({ name, isMulti, options, ...props }: TappSelectFilterProps<Option, IsMulti, Group>) {
  const navigate = useNavigateSearch();
  const [searchParams] = useSearchParams();

  const encodeQueryParam = (value: TappSelectOptionValue | TappSelectOptionValue[]) => {
    const stringifyValue = (val: TappSelectOptionValue) => {
      switch (typeof val) {
        case 'number':
          return val.toString();
        case 'string':
          return val;
        case 'boolean':
          return val.toString();
        case 'object':
          if (val instanceof Date) {
            return val.toISOString();
          } else {
            return JSON.stringify(val);
          }
      }
    };
    return Array.isArray(value) ? value.map(stringifyValue).join(',') : stringifyValue(value);
  };

  const decodeQueryParam = (value: string, isMulti: boolean | undefined) => {
    const decodeValue: (val: string) => TappSelectOptionValue = (val) => {
      try {
        const decoded = JSON.parse(val);
        if (typeof decoded === 'object') {
          if (decoded instanceof Array) {
            return decoded.map(decodeValue);
          }
          return decoded;
        }
        return decoded;
      } catch (_) {
        if (dayjs(val).isValid()) {
          return dayjs(val).toDate();
        }
        return val;
      }
    };

    if (isMulti) {
      return value.split(',').map(decodeValue);
    } else {
      return decodeValue(value);
    }
  };

  const currentValue = useMemo(() => {
    const param = searchParams.get(name);
    if (!param || !options) return null;

    const paramValue = decodeQueryParam(param, isMulti);

    if (Array.isArray(paramValue)) {
      const valuesArray = paramValue.map((val) => findOptionByValue(val, options)).filter((v) => v != null) as Option[];
      if (valuesArray.length === 0) return null;
      return valuesArray;
    } else {
      return findOptionByValue(paramValue, options);
    }
  }, [searchParams]);

  const onValueChange = (value: OnChangeValue<Option, IsMulti>) => {
    const params: Record<string, string | null> = {};

    if (!value) {
      params[name] = null;
    } else if (isMulti) {
      const val = value as MultiValue<TappSelectOption>;
      if (val.length === 0) {
        params[name] = null;
      } else {
        params[name] = encodeQueryParam(val.map((option) => option.value));
      }
    } else {
      params[name] = encodeQueryParam((value as TappSelectOption).value);
    }

    navigate({
      params,
      paramsHandling: 'merge',
    });
  };

  return (
    <TappSelect
      value={currentValue}
      options={options}
      isMulti={isMulti}
      onChange={(e) => onValueChange(e)}
      {...props}
    />
  );
}

TappSelectFilter.displayName = 'TappSelectFilter';
export { TappSelectFilter };
